import { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LOCALES, DEFAULT_LOCALE, type Locale, isSupportedLocale } from './i18n';
import PublicLayout from './components/layout/PublicLayout';
import AdminLayout from './components/layout/AdminLayout';
import Spinner from './components/ui/Spinner';
import CookieBanner from './components/layout/CookieBanner';
import { useTracking } from './hooks/useTracking';

// Lazy pages — code splitting por rota
const Home = lazy(() => import('./pages/Home'));
const Destinations = lazy(() => import('./pages/Destinations'));
const Packages = lazy(() => import('./pages/Packages'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Reservation = lazy(() => import('./pages/Reservation'));
const Confirmation = lazy(() => import('./pages/Confirmation'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Profile = lazy(() => import('./pages/Profile'));
const TransferPayment = lazy(() => import('./pages/TransferPayment'));
const PayPalCheckout = lazy(() => import('./pages/PayPalCheckout'));
const Signup = lazy(() => import('./pages/auth/Signup'));
const Login = lazy(() => import('./pages/auth/Login'));

const AdminLogin = lazy(() => import('./pages/admin/Login'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminBookings = lazy(() => import('./pages/admin/BookingsList'));
const AdminBookingDetail = lazy(() => import('./pages/admin/BookingDetail'));
const AdminLeads = lazy(() => import('./pages/admin/LeadsList'));
const AdminPackages = lazy(() => import('./pages/admin/PackagesList'));
const AdminPaymentProofs = lazy(() => import('./pages/admin/PaymentProofs'));
const AdminUsers = lazy(() => import('./pages/admin/UsersList'));

function LocaleGate() {
  const { locale } = useParams<{ locale: string }>();
  const { i18n } = useTranslation();
  const location = useLocation();

  if (!locale || !isSupportedLocale(locale)) {
    const detected = detectInitialLocale();
    return (
      <Navigate
        to={`/${detected}${location.pathname.replace(/^\/[^/]+/, '')}${location.search}`}
        replace
      />
    );
  }

  useEffect(() => {
    if (i18n.language !== locale) i18n.changeLanguage(locale);
    document.documentElement.lang = locale;
  }, [locale, i18n]);

  return (
    <Suspense fallback={<FullPageSpinner />}>
      <Routes>
        <Route element={<PublicLayout />}>
          <Route index element={<Home />} />
          <Route path="destinations" element={<Destinations />} />
          <Route path="destinos" element={<Destinations />} />
          <Route path="packages" element={<Packages />} />
          <Route path="pacotes" element={<Packages />} />
          <Route path="forfaits" element={<Packages />} />
          <Route path="paquetes" element={<Packages />} />
          <Route path="about" element={<About />} />
          <Route path="sobre" element={<About />} />
          <Route path="a-propos" element={<About />} />
          <Route path="contact" element={<Contact />} />
          <Route path="contato" element={<Contact />} />
          <Route path="contacto" element={<Contact />} />
          <Route path="contacto-es" element={<Contact />} />
          <Route path="reservation" element={<Reservation />} />
          <Route path="reserva" element={<Reservation />} />
          <Route path="reservation/checkout" element={<PayPalCheckout />} />
          <Route path="reserva/checkout" element={<PayPalCheckout />} />
          <Route path="reservation/confirmation" element={<Confirmation />} />
          <Route path="reserva/confirmacao" element={<Confirmation />} />
          {/* Consulta de reserva por token (?t=<uuid>) foi removida em 2026-08-05:
              dependia do role `customer`, que o Hasura nunca aceitou — so vale
              x-hasura-role que esteja nos allowed roles do JWT, e `customer`
              nunca esteve. O cliente ve as reservas logado, em /profile.
              Os links antigos caem la em vez de dar 404. */}
          <Route path="my-booking" element={<Navigate to="../profile" replace />} />
          <Route path="minha-reserva" element={<Navigate to="../perfil" replace />} />
          <Route path="ma-reservation" element={<Navigate to="../profil" replace />} />

          {/* Auth + perfil cliente */}
          <Route path="signup" element={<Signup />} />
          <Route path="cadastro" element={<Signup />} />
          <Route path="inscription" element={<Signup />} />
          <Route path="registro" element={<Signup />} />
          <Route path="login" element={<Login />} />
          <Route path="entrar" element={<Login />} />
          <Route path="connexion" element={<Login />} />
          <Route path="iniciar-sesion" element={<Login />} />
          <Route path="profile" element={<Profile />} />
          <Route path="perfil" element={<Profile />} />
          <Route path="profil" element={<Profile />} />

          {/* Comprovante transferencia */}
          <Route path="transfer-payment/:id" element={<TransferPayment />} />
          <Route path="transferencia/:id" element={<TransferPayment />} />
          <Route path="virement/:id" element={<TransferPayment />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function detectInitialLocale(): Locale {
  const saved = typeof window !== 'undefined' ? window.localStorage.getItem('preferred_locale') : null;
  if (saved && isSupportedLocale(saved)) return saved;

  if (typeof navigator !== 'undefined') {
    for (const lang of navigator.languages ?? [navigator.language]) {
      if (isSupportedLocale(lang)) return lang;
      const prefix = lang.split('-')[0];
      if (prefix === 'pt') return 'pt-BR';
      if (prefix && isSupportedLocale(prefix)) return prefix as Locale;
    }
  }

  return DEFAULT_LOCALE;
}

function FullPageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-sand-50">
      <Spinner size="lg" />
    </div>
  );
}

export default function App() {
  useTracking(); // GA4 + Clarity page views

  return (
    <>
      <a href="#main-content" className="skip-link">
        Pular para conteúdo principal
      </a>
      <Routes>
        <Route path="/admin" element={<AdminLayout />}>
          <Route path="login" element={<Suspense fallback={<FullPageSpinner />}><AdminLogin /></Suspense>} />
          <Route index element={<Suspense fallback={<FullPageSpinner />}><AdminDashboard /></Suspense>} />
          <Route path="bookings" element={<Suspense fallback={<FullPageSpinner />}><AdminBookings /></Suspense>} />
          <Route path="bookings/:id" element={<Suspense fallback={<FullPageSpinner />}><AdminBookingDetail /></Suspense>} />
          <Route path="leads" element={<Suspense fallback={<FullPageSpinner />}><AdminLeads /></Suspense>} />
          <Route path="packages" element={<Suspense fallback={<FullPageSpinner />}><AdminPackages /></Suspense>} />
          <Route path="payment-proofs" element={<Suspense fallback={<FullPageSpinner />}><AdminPaymentProofs /></Suspense>} />
          <Route path="users" element={<Suspense fallback={<FullPageSpinner />}><AdminUsers /></Suspense>} />
        </Route>

        <Route path="/:locale/*" element={<LocaleGate />} />
        <Route path="/" element={<Navigate to={`/${detectInitialLocale()}`} replace />} />
        <Route path="*" element={<Navigate to={`/${detectInitialLocale()}`} replace />} />
      </Routes>

      <CookieBanner />
    </>
  );
}

export { SUPPORTED_LOCALES };
